import { useState } from "react";
import { redirectToLogin } from "../../utils/auth";

const emptyRole = { platform_admin: false, poc: false, customer_support: false, rcs_poc: false, rcs_csm: false, pmo: false };

// Add a user (V2). RCS clients are onboarded via email (team/usertype auto-set on
// the server, no internal roles). Internal staff need a team + user type.
export const AddUserModal = ({ setIsAddModalOpen, onSaved, onSuccess, teams, usertypes, roleColumns }) => {
  const BASE = process.env.REACT_APP_BACKEND_URL;
  const [form, setForm] = useState({
    email: "",
    name: "",
    phone: "",
    clientType: "internal",
    team: "",
    usertype: "",
    altLoginEnabled: false,
    role: { ...emptyRole },
  });
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  const isRcs = form.clientType === "RCS";
  const set = (patch) => setForm((prev) => ({ ...prev, ...patch }));
  const toggleRole = (key) => setForm((prev) => ({ ...prev, role: { ...prev.role, [key]: !prev.role[key] } }));

  const save = async () => {
    setError("");
    const email = form.email.trim().toLowerCase();
    const name = form.name.trim();
    if (!name || !email) return setError("Name and email are required.");
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return setError("Enter a valid email.");
    if (!isRcs && (!form.team || !form.usertype)) return setError("Internal staff need a team and user type.");

    const phone = form.phone.trim() || null;
    const entry = isRcs
      ? { name, email, phone, clientType: "RCS" }
      : { name, email, phone, clientType: "internal", team: form.team, usertype: form.usertype, altLoginEnabled: form.altLoginEnabled, role: form.role };

    const authToken = localStorage.getItem("authToken");
    if (!authToken) { redirectToLogin(); return; }
    setSaving(true);
    try {
      const res = await fetch(`${BASE}/API/V2/users`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${authToken}` },
        body: JSON.stringify({ users: [entry] }),
      });
      const data = await res.json();
      if (res.ok) {
        onSaved && onSaved();
        setIsAddModalOpen(false);
        onSuccess &&
          onSuccess(
            isRcs
              ? `${name} has been onboarded. An onboarding email was sent to ${email}.`
              : `${name} has been added successfully.`,
          );
      } else {
        setError(data.error || "Failed to add user.");
      }
    } catch {
      setError("Network error. Please try again.");
    }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
      <div className="bg-white rounded-lg shadow-lg w-full max-w-md p-6">
        <h2 className="text-xl font-bold mb-4">Onboard User</h2>
        {error && (
          <div className="mb-3 px-3 py-2 bg-red-50 text-red-600 text-sm rounded border border-red-200">{error}</div>
        )}
        <form onSubmit={(e) => { e.preventDefault(); save(); }} className="space-y-4">
          <input
            type="email"
            placeholder="Email"
            value={form.email}
            onChange={(e) => set({ email: e.target.value })}
            className="w-full border px-3 py-2 rounded"
            required
          />
          <input
            type="text"
            placeholder="Name"
            value={form.name}
            onChange={(e) => set({ name: e.target.value })}
            className="w-full border px-3 py-2 rounded"
            required
          />
          <input
            type="tel"
            placeholder="Phone (e.g. +9199...) — for priority-ticket call alerts"
            value={form.phone}
            onChange={(e) => set({ phone: e.target.value })}
            className="w-full border px-3 py-2 rounded"
          />
          <select
            value={form.clientType}
            onChange={(e) => set({ clientType: e.target.value })}
            className="w-full border px-3 py-2 rounded"
          >
            <option value="internal">Internal Staff</option>
            <option value="RCS">RCS Client</option>
          </select>

          {!isRcs && (
            <>
              <select
                value={form.team}
                onChange={(e) => set({ team: e.target.value })}
                className="w-full border px-3 py-2 rounded"
              >
                <option value="">--Select Team--</option>
                {teams.map((t) => (
                  <option key={t.value} value={t.value}>{t.key}</option>
                ))}
              </select>
              <select
                value={form.usertype}
                onChange={(e) => set({ usertype: e.target.value })}
                className="w-full border px-3 py-2 rounded"
              >
                <option value="">--Select User Type--</option>
                {usertypes.map((u) => (
                  <option key={u.value} value={u.value}>{u.key}</option>
                ))}
              </select>
              <div className="grid grid-cols-2 gap-2 mt-2">
                {roleColumns.map((r) => (
                  <label key={r.key} className="flex items-center text-sm">
                    <input
                      type="checkbox"
                      checked={form.role[r.key]}
                      onChange={() => toggleRole(r.key)}
                      className="mr-2"
                    />
                    {r.label}
                  </label>
                ))}
              </div>
              <label className="flex items-center text-sm mt-1">
                <input
                  type="checkbox"
                  checked={form.altLoginEnabled}
                  onChange={(e) => set({ altLoginEnabled: e.target.checked })}
                  className="mr-2"
                />
                Alternate login (authenticator / email OTP)
              </label>
            </>
          )}
          {isRcs && (
            <p className="text-xs text-gray-400">
              RCS clients are onboarded automatically (24h email → set password → 2FA). No team or internal roles.
            </p>
          )}

          <div className="flex justify-end gap-2 mt-6">
            <button
              type="button"
              onClick={() => setIsAddModalOpen(false)}
              className="bg-gray-200 text-gray-700 px-4 py-2 rounded"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="bg-jiojbmpurple text-white px-4 py-2 rounded disabled:opacity-50"
            >
              {saving ? "Saving…" : "Save User"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
